import {useEffect, useState, Fragment} from "react";
import moment from "moment-timezone";
import "moment-timezone";

import useCustomTranslation from "core/customTranslation";
import translations from "../translations";

const BUCKETS = [
    {min: 0, max: 9, label: "0-9%"},
    {min: 10, max: 19, label: "10-19%"},
    {min: 20, max: 29, label: "20-29%"},
    {min: 30, max: 39, label: "30-39%"},
    {min: 40, max: 49, label: "40-49%"},
    {min: 50, max: 59, label: "50-59%"},
    {min: 60, max: 69, label: "60-69%"},
    {min: 70, max: 79, label: "70-79%"},
    {min: 80, max: 89, label: "80-89%"},
    {min: 90, max: 100, label: "90-100%"},
];

const PASTEL_PRIMARY = "rgba(var(--bs-primary-rgb), 0.22)";

const RangeListTable = ({itemsShown, dateHeaders, targetPercentage}) => {
    const {t: tC} = useCustomTranslation(translations);

    const renderVal = (val, isPct = false, decimals = false) => {
        if (val === null || val === undefined) return "-";
        if (typeof val !== "number") return val;
        const formatted = decimals ? val.toFixed(2).replace(".", ",") : val % 1 === 0 ? val : val.toFixed(2).replace(".", ",");
        return isPct ? formatted + "%" : formatted;
    };

    if (!itemsShown || itemsShown.length === 0) {
        return (
            <div className="col-12" style={{overflowX: "auto"}}>
                <hr className="mb-3" />
                <div className="alert alert-secondary text-center">{tC("filters.noData")}</div>
            </div>
        );
    }

    const threshold = targetPercentage || 100;

    const dateStats = {};
    dateHeaders.forEach((d) => {
        const total = itemsShown.filter((item) => item.daily_levels && d.date in item.daily_levels).length;
        dateStats[d.date] = {total, aboveThreshold: 0, buckets: {}};
        BUCKETS.forEach((b) => {
            dateStats[d.date].buckets[b.label] = 0;
        });
        itemsShown.forEach((item) => {
            const entry = item.daily_levels && item.daily_levels[d.date];
            if (entry && entry.value !== undefined && entry.value !== null) {
                for (const b of BUCKETS) {
                    if (entry.value >= b.min && entry.value <= b.max) {
                        dateStats[d.date].buckets[b.label]++;
                        break;
                    }
                }
                if (entry.value >= threshold) {
                    dateStats[d.date].aboveThreshold++;
                }
            }
        });
    });

    const totalDates = dateHeaders.length;

    const computeBuckets = () => {
        return BUCKETS.map((b) => {
            let sumPct = 0,
                sumCount = 0,
                validDates = 0;
            const daily = {};
            dateHeaders.forEach((d) => {
                const stat = dateStats[d.date];
                const count = stat.buckets[b.label] || 0;
                const pct = stat.total > 0 ? Math.round((count / stat.total) * 10000) / 100 : 0;
                daily[d.date] = {count, pct};
                sumCount += count;
                sumPct += pct;
                if (stat.total > 0) validDates++;
            });
            return {
                label: b.label,
                totalPct: validDates > 0 ? Math.round((sumPct / validDates) * 100) / 100 : 0,
                totalCount: totalDates > 0 ? Math.round((sumCount / totalDates) * 100) / 100 : 0,
                daily,
            };
        });
    };

    const computeTotal = () => {
        let sumCount = 0,
            validDates = 0;
        const daily = {};
        dateHeaders.forEach((d) => {
            const stat = dateStats[d.date];
            const count = stat.total;
            daily[d.date] = {count, pct: 100};
            sumCount += count;
            if (stat.total > 0) validDates++;
        });
        return {
            label: tC("tables.totalLabel"),
            totalPct: 100,
            totalCount: totalDates > 0 ? Math.round((sumCount / totalDates) * 100) / 100 : 0,
            daily,
        };
    };

    const computeAhorro = () => {
        let sumPct = 0,
            sumCount = 0,
            validDates = 0;
        const daily = {};
        dateHeaders.forEach((d) => {
            const stat = dateStats[d.date];
            const total = stat.total;
            const above = stat.aboveThreshold || 0;
            const saved = total - above;
            // Joaquin quiere above, no saved
            const pct = total > 0 ? Math.round((above / total) * 10000) / 100 : 0;
            daily[d.date] = {count: saved, pct};
            sumPct += pct;
            sumCount += saved;
            if (stat.total > 0) validDates++;
        });
        return {
            label: tC("tables.savings"),
            totalPct: validDates > 0 ? Math.round((sumPct / validDates) * 100) / 100 : 0,
            totalCount: totalDates > 0 ? Math.round((sumCount / totalDates) * 100) / 100 : 0,
            daily,
        };
    };

    const bucketRows = computeBuckets();
    const totalRow = computeTotal();
    const ahorroRow = computeAhorro();

    const isThresholdRange = (bucketMax) => {
        return bucketMax >= threshold;
    };

    const thPctStyle = {
        textAlign: "center",
        verticalAlign: "middle",
        width: "70px",
        borderBottom: "1px solid #7e7e7e",
    };
    const thCountStyle = {
        textAlign: "center",
        verticalAlign: "middle",
        width: "70px",
        borderBottom: "1px solid #7e7e7e",
        borderRight: "1px solid #7e7e7e",
    };
    const tdPctStyle = {
        textAlign: "center",
        verticalAlign: "middle",
        width: "70px",
    };
    const tdCountStyle = {
        textAlign: "center",
        verticalAlign: "middle",
        width: "70px",
    };

    return (
        <div className="col-12" style={{overflowX: "auto"}}>
            <hr className="mb-3" />
            <table className="table table-striped">
                <thead>
                    <tr>
                        <th
                            rowSpan="2"
                            className="text-center border-start"
                            style={{
                                borderBottom: "1px solid #7e7e7e",
                                borderRight: "1px solid #7e7e7e",
                                verticalAlign: "middle",
                                width: "30px",
                            }}>
                            {tC("tables.rangePctFill")}
                        </th>
                        {dateHeaders.map((d, i) => (
                            <th
                                key={"dh-" + i}
                                colSpan="2"
                                className="text-center border-start"
                                style={{borderBottom: "1px solid #7e7e7e", verticalAlign: "middle"}}>
                                {d.label}
                            </th>
                        ))}
                        <th
                            colSpan="2"
                            className="text-center border-start"
                            style={{borderBottom: "1px solid #7e7e7e", borderLeft: "1px solid #7e7e7e", verticalAlign: "middle"}}>
                            {tC("tables.totalSummary")}
                        </th>
                    </tr>
                    <tr>
                        {dateHeaders.map((d, i) => (
                            <Fragment key={"th-" + i}>
                                <th className="text-center border-start" style={thPctStyle}>
                                    {tC("tables.pctContainer")}
                                </th>
                                <th className="text-center" style={thCountStyle}>
                                    {tC("tables.containers")}
                                </th>
                            </Fragment>
                        ))}
                        <th className="text-center border-start" style={thPctStyle}>
                            {tC("tables.pctContainer")}
                        </th>
                        <th className="text-center" style={thCountStyle}>
                            {tC("tables.containers")}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {bucketRows.map((row, ri) => {
                        const highlighted = isThresholdRange(BUCKETS[ri].max);
                        return (
                            <tr key={"hlri-" + ri} data-key={"hlri-" + ri}>
                                <td className="border-start" style={{borderRight: "1px solid #7e7e7e", paddingLeft: "8px"}}>
                                    {row.label}
                                </td>
                                {dateHeaders.map((d, i) => (
                                    <Fragment key={"hlrip-" + i}>
                                        <td
                                            className="text-center border-start"
                                            style={{
                                                ...tdPctStyle,
                                                backgroundColor: highlighted ? PASTEL_PRIMARY : "transparent",
                                            }}>
                                            {renderVal(row.daily[d.date]?.pct ?? null, true)}
                                        </td>
                                        <td
                                            className="text-center"
                                            style={{
                                                ...tdCountStyle,
                                                backgroundColor: highlighted ? PASTEL_PRIMARY : "transparent",
                                            }}>
                                            {renderVal(row.daily[d.date]?.count ?? null)}
                                        </td>
                                    </Fragment>
                                ))}
                                <td
                                    className="text-center border-start"
                                    style={{
                                        ...tdPctStyle,
                                        fontWeight: "bold",
                                        backgroundColor: highlighted ? PASTEL_PRIMARY : "transparent",
                                    }}>
                                    {renderVal(row.totalPct, true, true)}
                                </td>
                                <td
                                    className="text-center"
                                    style={{...tdCountStyle, backgroundColor: highlighted ? PASTEL_PRIMARY : "transparent"}}>
                                    {renderVal(row.totalCount, false, true)}
                                </td>
                            </tr>
                        );
                    })}
                    <tr style={{fontWeight: "bold", borderTop: "2px solid #333"}}>
                        <td className="border-start" style={{borderRight: "1px solid #7e7e7e", paddingLeft: "8px"}}>
                            {totalRow.label}
                        </td>
                        {dateHeaders.map((d, i) => (
                            <Fragment key={"tp-" + i}>
                                <td className="text-center border-start" style={tdPctStyle}>
                                    {renderVal(totalRow.daily[d.date]?.pct ?? null, true)}
                                </td>
                                <td className="text-center" style={tdCountStyle}>
                                    {renderVal(totalRow.daily[d.date]?.count ?? null)}
                                </td>
                            </Fragment>
                        ))}
                        <td className="text-center border-start" style={tdPctStyle}>
                            {renderVal(totalRow.totalPct, true, true)}
                        </td>
                        <td className="text-center" style={tdCountStyle}>
                            {renderVal(totalRow.totalCount, false, true)}
                        </td>
                    </tr>
                    <tr>
                        <td
                            className="border-start"
                            style={{borderRight: "1px solid #7e7e7e", paddingLeft: "8px", fontWeight: "bold"}}>
                            {ahorroRow.label}
                        </td>
                        {dateHeaders.map((d, i) => (
                            <Fragment key={"ap-" + i}>
                                <td
                                    className="text-center border-start"
                                    style={{...tdPctStyle, fontWeight: "bold", backgroundColor: PASTEL_PRIMARY}}>
                                    {renderVal(ahorroRow.daily[d.date]?.pct ?? null, true)}
                                </td>
                                <td className="text-center" style={{...tdCountStyle, backgroundColor: PASTEL_PRIMARY}}>
                                    {renderVal(ahorroRow.daily[d.date]?.count ?? null)}
                                </td>
                            </Fragment>
                        ))}
                        <td
                            className="text-center border-start"
                            style={{...tdPctStyle, fontWeight: "bold", backgroundColor: PASTEL_PRIMARY}}>
                            {renderVal(ahorroRow.totalPct, true, true)}
                        </td>
                        <td className="text-center" style={{...tdCountStyle, backgroundColor: PASTEL_PRIMARY}}>
                            {renderVal(ahorroRow.totalCount)}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default RangeListTable;
